import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { TextArea } from '../input/TextArea'
import { Input } from '../input/Input'
import { Button } from '../buttons/Button'
import { Stack } from '../Stack'
import { Select } from '../Select'
import { Loader } from '../Loader'

type Payload = {
  regNr: string
  productType: string
  company: string
  address?: string
  customerName: string
  postalCode: string
  postCity: string
  additionalComment: string
}

type AdditionalProduct = {
  AgreementCode?: string
  Description?: string
  ReferenceNo?: string
}

type Car = {
  registrationNumber?: string
  additionalProducts?: AdditionalProduct[]
}

const lostFuelCardSchema = z.object({
  regNumber: z.string().min(1, 'Registreringsnummer er obligatorisk'),
  cardChipType: z.string().min(1, 'Type kort/brikke er obligatorisk'),
  company: z.string().min(1, 'Selskap er obligatorisk'),
  customerName: z.string().min(1, 'Navn er obligatorisk'),
  address: z.string().min(1, 'Adresse er obligatorisk'),
  postalCode: z.string().min(1, 'Postnummer er obligatorisk'),
  postCity: z.string().min(1, 'Poststed er obligatorisk'),
  additionalComment: z.string(),
})

type LostFuelCardFormData = z.infer<typeof lostFuelCardSchema>

interface LostFuelCardProps {
  car: Car
  setShowLostFuelCard: (show: boolean) => void
  defaultData?: Partial<LostFuelCardFormData>
  onSubmit?: (payload: Payload) => void | Promise<void>
}

export const LostFuelCard = ({ car, setShowLostFuelCard, defaultData, onSubmit }: LostFuelCardProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [success, setSuccess] = useState(false)

  const { additionalProducts = [] } = car
  const productOptions = additionalProducts
    .map((product) => {
      const value = product.ReferenceNo || product.AgreementCode
      if (!value) {
        return null
      }

      return {
        value,
        label: product.Description || value,
      }
    })
    .filter((product): product is { value: string; label: string } => product !== null)

  const { register, control, handleSubmit, formState } = useForm<LostFuelCardFormData>({
    resolver: zodResolver(lostFuelCardSchema),
    defaultValues: {
      regNumber: defaultData?.regNumber ?? car.registrationNumber ?? '',
      cardChipType: defaultData?.cardChipType ?? productOptions[0]?.value ?? '',
      company: defaultData?.company ?? '',
      address: defaultData?.address ?? '',
      customerName: defaultData?.customerName ?? '',
      postalCode: defaultData?.postalCode ?? '',
      postCity: defaultData?.postCity ?? '',
      additionalComment: defaultData?.additionalComment ?? '',
    },
  })

  const submit = handleSubmit(async (values) => {
    setIsError(false)
    setSuccess(false)
    setIsLoading(true)

    const formattedPayload: Payload = {
      regNr: values.regNumber,
      productType: values.cardChipType,
      company: values.company,
      address: values.address,
      customerName: values.customerName,
      postalCode: values.postalCode,
      postCity: values.postCity,
      additionalComment: values.additionalComment ?? '',
    }

    try {
      await onSubmit?.(formattedPayload)
      setSuccess(true)
    } catch {
      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  })

  if (productOptions.length === 0) {
    return (
      <div className="ap:flex ap:w-full ap:items-center ap:justify-center ap:min-h-[200px] ap:flex-col ap:gap-2xl">
        <p className="ap:text-center ap:max-w-[40ch]">Du har ingen kort eller brikker som kan bestilles.</p>
        <Button variant="secondary" onClick={() => setShowLostFuelCard(false)}>
          Lukk vinduet
        </Button>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="ap:flex ap:w-full ap:items-center ap:justify-center ap:text-content-danger ap:min-h-[200px] ap:flex-col ap:gap-2xl ap:overflow-y-auto">
        <p className="ap:text-center ap:max-w-[40ch]">
          Noe gikk galt under bestilling av kort/chip. Proev igjen senere eller kontakt Autoplan.
        </p>
        <Button variant="secondary" onClick={() => setShowLostFuelCard(false)}>
          Lukk vinduet
        </Button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="ap:flex ap:w-full ap:items-center ap:justify-center ap:min-h-[200px] ap:flex-col ap:gap-2xl">
        <Loader />
        <p>Sender forespoersel...</p>
      </div>
    )
  }

  if (success) {
    return (
      <div className="ap:flex ap:w-full ap:items-center ap:justify-center ap:min-h-[200px] ap:flex-col ap:gap-2xl">
        <p className="ap:text-xl">Nytt kort/brikke er bestilt!</p>
        <Button variant="secondary" onClick={() => setShowLostFuelCard(false)}>
          Lukk vinduet
        </Button>
      </div>
    )
  }

  return (
    <form className="ap:flex ap:flex-col ap:gap-3xl ap:items-start" onSubmit={submit}>
      <p className="ap:text-base">Kortet/brikken vil bli sendt til din registrerte adresse.</p>
      <Stack spacing="xl">
        <div className="ap:flex ap:flex-row ap:items-center ap:justify-between ap:w-full ap:gap-md">
          <Input label="Registreringsnummer" error={formState.errors.regNumber?.message} {...register('regNumber')} />
          <Controller
            name="cardChipType"
            control={control}
            render={({ field }) => (
              <Select
                label="Type kort/brikke"
                options={productOptions}
                value={field.value}
                onValueChange={field.onChange}
                placeholder="Velg kort eller brikke"
              />
            )}
          />
          {formState.errors.cardChipType?.message && (
            <p className="ap:text-content-danger">{formState.errors.cardChipType.message}</p>
          )}
        </div>
        <Input label="Selskap" error={formState.errors.company?.message} {...register('company')} />
        <Input label="Navn" error={formState.errors.customerName?.message} {...register('customerName')} />
        <Input label="Adresse" error={formState.errors.address?.message} {...register('address')} />
        <div className="ap:flex ap:flex-row ap:items-center ap:justify-between ap:w-full ap:gap-md">
          <Input label="Postnummer" error={formState.errors.postalCode?.message} {...register('postalCode')} />
          <Input label="Poststed" error={formState.errors.postCity?.message} {...register('postCity')} />
        </div>
        <TextArea
          label="Kommentar"
          placeholder="Skriv en kommentar"
          error={formState.errors.additionalComment?.message}
          {...register('additionalComment')}
        />
      </Stack>
      <Button variant="secondary" onClick={submit} disabled={formState.isSubmitting || isLoading}>
        Bestill
      </Button>
    </form>
  )
}
